<template>
  <v-row>
    <v-col cols="12">
      <v-card class="acct-card" flat>
        <!-- HEADER BAND -->
        <div class="acct-header pl-base-bg">
          <div class="acct-header-icon">
            <v-icon color="#fff" size="22">mdi-account</v-icon>
          </div>
          <div>
            <div class="acct-title">My Account</div>
            <div class="acct-subtitle">Manage your profile and login details</div>
          </div>
        </div>

        <v-form ref="form" v-model="valid" class="pa-6">
          <!-- PROFILE PICTURE -->
          <div class="d-flex align-center mb-6">
            <div class="profile-avatar-wrap">
              <v-avatar size="72" class="profile-avatar">
                <v-img v-if="preview" :src="preview" />
                <span v-else class="white--text text-h5">
                  {{ user?.name?.charAt(0)?.toUpperCase() }}
                </span>
              </v-avatar>

              <v-btn
                icon
                small
                class="profile-avatar-edit"
                @click="$refs.fileInput.click()"
              >
                <v-icon size="16" color="#fff">mdi-camera</v-icon>
              </v-btn>

              <input
                ref="fileInput"
                type="file"
                accept="image/*"
                class="d-none"
                @change="onFileSelected"
              />
            </div>

            <div class="ml-4">
              <div class="acct-name">{{ user?.name }}</div>
              <div>
                <a class="profile-avatar-action" @click="$refs.fileInput.click()">
                  Change photo
                </a>
                <template v-if="preview">
                  <span class="mx-1 grey--text">&middot;</span>
                  <a class="profile-avatar-action profile-avatar-remove" @click="removeFile">
                    Remove
                  </a>
                </template>
              </div>
            </div>
          </div>

          <div class="acct-section-label">Account Details</div>
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.name"
                label="Full Name"
                outlined
                dense
                :rules="[rules.required]"
                prepend-inner-icon="mdi-account"
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.email"
                label="Email"
                outlined
                dense
                :rules="[rules.required, rules.email]"
                prepend-inner-icon="mdi-email"
                disabled
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.phone"
                label="Phone"
                outlined
                dense
                :rules="[rules.required]"
                prepend-inner-icon="mdi-phone"
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.username"
                label="Username (optional)"
                outlined
                dense
                :rules="[rules.username]"
                prepend-inner-icon="mdi-at"
                hint="You can log in with this instead of your email"
                persistent-hint
                @input="form.username = form.username.toLowerCase()"
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.password"
                label="Password"
                outlined
                dense
                :append-icon="passwordVisible ? 'mdi-eye-off' : 'mdi-eye'"
                @click:append="passwordVisible = !passwordVisible"
                :type="passwordVisible ? 'text' : 'password'"
                placeholder="Leave blank to keep current"
                prepend-inner-icon="mdi-lock"
              />
            </v-col>
          </v-row>

          <!-- SUBSCRIPTION (read-only) -->
          <div v-if="billingExpiryDate" class="acct-billing-row">
            <v-icon size="18" color="#64748b">mdi-calendar-clock-outline</v-icon>
            <div>
              <div class="acct-billing-label">
                {{ isTeamMember ? "Owner's subscription valid until" : "Subscription valid until" }}
              </div>
              <div class="acct-billing-value">{{ formattedBillingExpiry }}</div>
            </div>
          </div>

          <!-- ACTIONS -->
          <v-row class="mt-4" justify="end" align="center" no-gutters>
            <v-btn color="secondary" @click="reset">Reset</v-btn>

            <v-btn
              class="acct-save-btn ml-3"
              :loading="loading"
              @click="submit"
            >
              Update
            </v-btn>
          </v-row>
        </v-form>

        <v-divider />

        <!-- DANGER ZONE -->
        <div class="pa-6">
          <div class="acct-section-label">Danger Zone</div>
          <div class="d-flex align-center justify-space-between flex-wrap">
            <div class="mr-4">
              <div class="acct-danger-title">Delete Account</div>
              <div class="acct-danger-sub">
                Permanently delete your account and all associated data. This cannot be undone.
              </div>
            </div>
            <v-btn
              outlined
              color="error"
              class="mt-2"
              @click="deleteDialog = true"
            >
              Delete Account
            </v-btn>
          </div>
        </div>
      </v-card>
    </v-col>

    <!-- DELETE ACCOUNT CONFIRMATION -->
    <global-dialog
      v-model="deleteDialog"
      :buttons="deleteDialogButtons"
      title="Delete Account?"
      icon="mdi-alert-circle-outline"
    >
      <v-alert type="error" dense outlined class="mb-3">
        This will permanently delete your account, including your profile,
        business details, customers, products, invoices, transactions and
        all uploaded files. <strong>This action cannot be undone.</strong>
      </v-alert>
      Type <strong>DELETE</strong> below to confirm.
      <v-text-field
        v-model="deleteConfirmText"
        outlined
        dense
        class="mt-3"
        placeholder="DELETE"
        autofocus
      />
    </global-dialog>
  </v-row>
</template>

<script>
import { mapActions } from "vuex";
import GlobalDialog from "./common/global-dialog.vue";

export default {
  name: "MyAccount",
  components: { GlobalDialog },

  data() {
    return {
      valid: false,
      loading: false,
      passwordVisible: false,
      file: null,
      preview: null,
      docState: "keep",
      deleteDialog: false,
      deleteConfirmText: "",
      deleting: false,
      form: {
        name: "",
        email: "",
        username: "",
        phone: "",
        password: "",
      },
      rules: {
        required: (v) => !!v || "Required",
        email: (v) => /.+@.+\..+/.test(v) || "Invalid email",
        username: (v) =>
          !v || /^[a-z0-9_.]{4,20}$/.test(v) ||
          "4-20 chars: lowercase letters, numbers, underscore, dot",
      },
    };
  },

  computed: {
    user() {
      return this.$store.state.auth.user;
    },

    // A team member's own paymentExpiryDate is always null — the server
    // resolves the OWNER's date instead and sends it as billingExpiryDate
    // on both /auth/login and /auth/me, so this is already correct for
    // both roles with no branching needed here beyond the label wording.
    billingExpiryDate() {
      return this.user?.billingExpiryDate || null;
    },

    isTeamMember() {
      return !!this.user?.ownerUserId;
    },

    formattedBillingExpiry() {
      if (!this.billingExpiryDate) return "";
      return new Date(this.billingExpiryDate).toLocaleDateString("en-IN", { dateStyle: "medium" });
    },

    deleteDialogButtons() {
      return [
        {
          text: "Cancel",
          action: this.closeDeleteDialog,
          outlined: true,
          color: "#0a5a90",
        },
        {
          text: "Delete My Account",
          action: this.confirmDeleteAccount,
          color: "error",
          disabled: this.deleteConfirmText !== "DELETE",
          loading: this.deleting,
        },
      ];
    },
  },

  created() {
    this.loadUser();
  },

  methods: {
    ...mapActions("users", ["updateUser", "deleteMyAccount"]),

    loadUser() {
      if (!this.user) return;

      const { name, email, username, phone, profilePicture } = this.user;
      this.form = {
        name,
        email,
        username: username || "",
        phone,
        password: "",
      };

      this.preview = profilePicture || null;
      this.file = null;
      this.docState = "keep";
    },

    onFileSelected(event) {
      const file = event.target.files[0];
      event.target.value = "";
      if (!file) return;

      this.file = file;
      this.preview = URL.createObjectURL(file);
      this.docState = "replace";
    },

    removeFile() {
      this.file = null;
      this.preview = null;
      this.docState = "remove";
    },

    async submit() {
      if (!this.$refs.form.validate()) return;

      this.loading = true;

      try {
        const payload = { ...this.form };
        if (!payload.password) delete payload.password;

        if (this.file) {
          payload.file = await new Promise((res) => {
            const reader = new FileReader();
            reader.onload = () => res(reader.result);
            reader.readAsDataURL(this.file);
          });
          payload.fileName = this.file.name;
          payload.fileType = this.file.type;
        } else if (this.docState === "remove") {
          payload.removeFile = true;
        }

        await this.updateUser({
          id: this.user._id,
          payload,
        });

        this.$toast?.success("Profile updated successfully");
      } catch (err) {
        this.$toast?.error(
          err?.response?.data?.message || "Failed to update profile",
        );
      } finally {
        this.loading = false;
      }
    },

    reset() {
      this.loadUser();
    },

    closeDeleteDialog() {
      this.deleteDialog = false;
      this.deleteConfirmText = "";
    },

    async confirmDeleteAccount() {
      this.deleting = true;
      try {
        await this.deleteMyAccount();
        this.$toast?.success("Your account has been deleted");
        // Server already destroyed the session as part of the delete — just
        // clear local auth state instead of calling /auth/logout again.
        this.$store.commit("auth/LOGOUT");
        this.$router.replace("/login");
      } catch (err) {
        this.$toast?.error(
          err?.response?.data?.message || "Failed to delete account",
        );
      } finally {
        this.deleting = false;
        this.deleteDialog = false;
        this.deleteConfirmText = "";
      }
    },
  },
};
</script>

<style scoped>
.acct-card {
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(15, 30, 50, 0.1);
}

.acct-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 20px;
}

.acct-header-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.acct-title {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.01em;
}

.acct-subtitle {
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.75);
  margin-top: 2px;
}

.acct-name {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 4px;
}

.acct-section-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94a3b8;
  margin-bottom: 10px;
}

.acct-billing-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  margin-top: 4px;
  background: #f8fafc;
  border: 1px solid #eef1f5;
  width: fit-content;
}

.acct-billing-label {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
}

.acct-billing-value {
  font-size: 13.5px;
  font-weight: 700;
  color: #0f172a;
}

.acct-save-btn {
  background: var(--pl-base-bg-gradient) !important;
  color: #fff !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}

.profile-avatar {
  background: var(--pl-base-bg-gradient);
}

.profile-avatar-wrap {
  position: relative;
  width: 72px;
  height: 72px;
}

.profile-avatar-edit {
  position: absolute !important;
  bottom: -2px;
  right: -2px;
  background: var(--pl-base-bg-gradient) !important;
  border: 2px solid #ffffff;
  width: 28px !important;
  height: 28px !important;
}

.profile-avatar-action {
  font-size: 13px;
  font-weight: 600;
  color: #0a5a90;
  text-decoration: none;
  cursor: pointer;
}

.profile-avatar-action:hover {
  text-decoration: underline;
}

.profile-avatar-remove {
  color: #b23a3a;
}

.acct-danger-title {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
}

.acct-danger-sub {
  font-size: 12.5px;
  color: #64747f;
  max-width: 420px;
}
</style>
